import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, Send, Sparkles } from "lucide-react";
import { CONTACT, SERVICES_DROPDOWN, BUDGETS, whatsappLink } from "../content/site";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-[border-color,box-shadow] duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/10 outline-none";

const Field = ({ label, children, required, action }) => (
  <label className="block">
    <span className="flex items-center justify-between text-xs font-medium text-slate-600 mb-1.5">
      <span>{label} {required && <span className="text-[#0055FF]">*</span>}</span>
      {action}
    </span>
    {children}
  </label>
);

const WhatsAppIconSmall = () => (
  <svg viewBox="0 0 32 32" fill="currentColor" className="w-5 h-5" aria-hidden="true">
    <path d="M16.04 2c-7.73 0-14 6.27-14 14 0 2.47.65 4.88 1.87 7L2 30l7.18-1.88c2.06 1.12 4.37 1.71 6.86 1.71 7.73 0 14-6.27 14-14s-6.27-14-14-14zm0 25.6c-2.14 0-4.24-.58-6.07-1.66l-.44-.26-4.26 1.12 1.14-4.15-.29-.45a11.55 11.55 0 0 1-1.78-6.2c0-6.42 5.22-11.64 11.7-11.64 6.42 0 11.64 5.22 11.64 11.64.06 6.42-5.22 11.6-11.64 11.6zm6.4-8.72c-.35-.18-2.07-1.02-2.4-1.14-.32-.12-.56-.18-.8.18-.23.35-.91 1.14-1.12 1.37-.2.24-.41.27-.76.09-.35-.18-1.48-.55-2.82-1.74-1.04-.93-1.75-2.08-1.95-2.43-.21-.35-.02-.54.15-.72.16-.16.35-.41.53-.61.17-.21.23-.35.35-.58.11-.24.05-.44-.03-.62-.09-.18-.8-1.92-1.09-2.63-.29-.69-.58-.6-.8-.61h-.68c-.24 0-.62.09-.94.44-.32.35-1.24 1.21-1.24 2.96 0 1.74 1.27 3.43 1.45 3.67.17.23 2.5 3.82 6.06 5.35.85.37 1.51.59 2.03.75.85.27 1.63.23 2.24.14.68-.1 2.07-.85 2.37-1.66.29-.82.29-1.52.2-1.66-.08-.15-.32-.24-.67-.42z" />
  </svg>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", website_url: "", service: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiIdea, setAiIdea] = useState("");
  const [aiBrief, setAiBrief] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent — we'll be in touch shortly.", { description: "Prefer a faster reply? Message us on WhatsApp below." });
      setForm({ name: "", email: "", phone: "", business_name: "", website_url: "", service: "", budget: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your enquiry.", { description: "Please try again, or message us directly on WhatsApp." });
    } finally {
      setSending(false);
    }
  };

  const generateBrief = async () => {
    if (!aiIdea.trim() || aiBusy) return;
    setAiBusy(true);
    setAiBrief("");
    try {
      const res = await fetch(`${API}/ai/brief`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idea: aiIdea, business_name: form.business_name || undefined }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setAiBrief(full);
      }
    } catch {
      toast.error("The AI brief generator is unavailable right now.");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-[#F0F4FF] overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_1.15fr] gap-16">
        <Reveal>
          <SectionHeading
            overline="Get in touch"
            title="Let's build something great."
            sub="Have an idea, an outdated website, or simply need someone to take care of the technical side? Tell us what you need and we'll take it from there."
          />
          <div className="mt-10 space-y-4">
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card"
              className="flex items-center gap-4 bg-white border border-slate-200 soft-shadow rounded-2xl p-5 transition-[border-color,transform] duration-200 hover:border-[#25D366]/60 hover:-translate-y-0.5 group">
              <span className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center text-[#1fb857]"><WhatsAppIconSmall /></span>
              <div>
                <p className="text-sm font-semibold text-slate-900">WhatsApp — fastest reply</p>
                <p className="text-sm text-slate-500">{CONTACT.phoneDisplay}</p>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-card"
              className="flex items-center gap-4 bg-white border border-slate-200 soft-shadow rounded-2xl p-5 transition-[border-color,transform] duration-200 hover:border-[#0055FF]/50 hover:-translate-y-0.5">
              <span className="w-12 h-12 rounded-xl bg-[#F0F4FF] flex items-center justify-center"><Mail className="w-5 h-5 text-[#0055FF]" /></span>
              <div>
                <p className="text-sm font-semibold text-slate-900">Email us</p>
                <p className="text-sm text-slate-500">{CONTACT.email}</p>
              </div>
            </a>
          </div>
          <p className="mt-8 text-sm text-slate-500 leading-relaxed">
            What happens next: you send your enquiry → we review your requirements → we contact you to discuss the project and pricing → your project begins once everything is agreed.
          </p>
          <p className="mt-4 text-sm text-slate-500 leading-relaxed">
            You'll deal directly with me — Ismail — from the first message to launch day. No ticket systems, no call centres, no being passed around.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <form onSubmit={submit} data-testid="enquiry-form" className="bg-white border border-slate-200 soft-shadow rounded-3xl p-6 sm:p-9 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name" required>
                <input required value={form.name} onChange={set("name")} data-testid="form-name" placeholder="Your name" className={inputCls} autoComplete="name" />
              </Field>
              <Field label="Email" required>
                <input required type="email" value={form.email} onChange={set("email")} data-testid="form-email" placeholder="you@business.co.uk" className={inputCls} autoComplete="email" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone">
                <input type="tel" value={form.phone} onChange={set("phone")} data-testid="form-phone" placeholder="07000 000000" className={inputCls} autoComplete="tel" />
              </Field>
              <Field label="Business name">
                <input value={form.business_name} onChange={set("business_name")} data-testid="form-business" placeholder="Your business" className={inputCls} autoComplete="organization" />
              </Field>
            </div>
            <Field label="Current website URL (if you have one)">
              <input type="url" value={form.website_url} onChange={set("website_url")} data-testid="form-website" placeholder="https://" className={inputCls} autoComplete="url" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Service required">
                <select value={form.service} onChange={set("service")} data-testid="form-service" className={`${inputCls} appearance-none`}>
                  <option value="">Select a service…</option>
                  {SERVICES_DROPDOWN.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Budget">
                <select value={form.budget} onChange={set("budget")} data-testid="form-budget" className={`${inputCls} appearance-none`}>
                  <option value="">Select a range…</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Project description" required
              action={
                <button type="button" onClick={() => setAiOpen(!aiOpen)} data-testid="ai-brief-toggle"
                  className="inline-flex items-center gap-1.5 text-cyan-600 hover:text-cyan-700 transition-colors duration-200 font-medium">
                  <Sparkles className="w-3.5 h-3.5" /> {aiOpen ? "Hide AI helper" : "AI: help me write this"}
                </button>
              }>
              <div>
                {aiOpen && (
                  <div className="mb-3 rounded-2xl border border-cyan-200 bg-cyan-50/60 p-4" data-testid="ai-brief-panel">
                    <p className="text-xs text-slate-600 mb-3">Describe your idea in a sentence or two — our AI will turn it into a structured brief with a suggested package and estimated price.</p>
                    <div className="flex gap-2">
                      <input value={aiIdea} onChange={(e) => setAiIdea(e.target.value)} data-testid="ai-brief-input"
                        placeholder="e.g. I run a bakery and need a site with a menu and order enquiries"
                        className="flex-1 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-cyan-500 transition-colors duration-200" />
                      <button type="button" onClick={generateBrief} disabled={aiBusy || !aiIdea.trim()} data-testid="ai-brief-generate"
                        className="shrink-0 inline-flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors duration-200">
                        {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Generate
                      </button>
                    </div>
                    {aiBrief && (
                      <div className="mt-3">
                        <pre data-testid="ai-brief-output" className="whitespace-pre-wrap font-[inherit] text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-4 max-h-56 overflow-y-auto">{aiBrief}</pre>
                        <button type="button" data-testid="ai-brief-use"
                          onClick={() => { setForm({ ...form, message: aiBrief }); setAiOpen(false); toast.success("Brief added — edit it however you like."); }}
                          className="mt-3 inline-flex items-center gap-1.5 bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">
                          Use this brief
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <textarea required rows={4} value={form.message} onChange={set("message")} data-testid="form-message"
                  placeholder="Tell us about your project — what does your business do, and what do you need your website to achieve?"
                  className={`${inputCls} resize-none`} />
              </div>
            </Field>

            <button type="submit" disabled={sending} data-testid="form-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white font-semibold px-7 py-4 rounded-full transition-colors duration-200 glow-blue">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : "Request My Free Quote"}
            </button>
            <p className="text-center text-xs text-slate-400">No obligation. We'll reply with honest advice and a clear price.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
